import {
  Box,
  Checkbox,
  Divider,
  Flex,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  StackDivider,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
  Wrap,
} from "@chakra-ui/react";
import { Button } from "components/ds";
import MultiSelect from "components/MultiSelect";
import { useLoadMore } from "modules/useLoadMore";
import * as R from "ramda";
import { useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiFilter,
  FiPlay,
  FiRewind,
  FiTrash,
  FiUserPlus,
  FiUsers,
  FiXCircle,
} from "react-icons/fi";
import { HiOutlineMinusCircle } from "react-icons/hi";
import {
  MFAccessReviewAccount,
  MFAccessReviewWithTotals,
  MFAccountAccessReviewFilters,
} from "state/members/accessReviews";
import MFMembers from "state/members/membersState";
import { Option } from "state/options";
import SearchBox from "ui/SearchBox";
import { useDebounce } from "use-debounce";
import AccountRow from "./AccountRow";
import ReviewInfoSection from "./ReviewInfoSection";
import { MF_OPTIONS } from "state/members/options";
import FilterDropdown from "components/FilterDropdown";
import { upperFirst } from "modules/utils";
import { FaIdBadge } from "react-icons/fa";
import { MdLocalFireDepartment } from "react-icons/md";

const AccountsList = ({
  accessReviewWithTotal,
}: {
  accessReviewWithTotal: MFAccessReviewWithTotals;
}) => {
  const accessReviewId = accessReviewWithTotal.id;
  const [filters, setFilters] = useState<MFAccountAccessReviewFilters>({
    access_review_id: accessReviewId,
    missing_role: false,
  });
  const { data, fetchNextPage, isLoading, isFetching, remove } =
    MFMembers.useListAccessReviewAccounts(filters, 1000);
  const [search, setSearch] = useState("");
  const [debounced] = useDebounce(search, 300);
  useEffect(() => {
    setFilters((f) => ({
      ...f,
      search: debounced?.length ? debounced : undefined,
    }));
  }, [debounced]);

  const loader = useLoadMore(fetchNextPage, isFetching);
  const rows = data?.pages.flatMap((tl) => tl.rows) || [];

  useEffect(() => {
    setFilters((f) =>
      f.access_review_id !== accessReviewWithTotal.id
        ? { ...f, access_review_id: accessReviewWithTotal.id }
        : f,
    );
  }, [accessReviewWithTotal.id]);

  const roles = useMemo(() => {
    return R.uniq(
      (accessReviewWithTotal.roles || [])
        .flat()
        .map((r) => ({ label: r, value: r }))
        .concat(
          rows
            .map((r) => r.role)
            .flat()
            .map((r) => ({ label: r, value: r })),
        ),
    );
  }, [rows, accessReviewWithTotal.roles]);

  const options = MF_OPTIONS.useListOptions();
  const [selectedMember, setSelectedMember] = useState<Option | null>(null);
  const [selectedRole, setSelectedRole] = useState<Option | null>(null);
  const toast = useToast();
  const { mutate: addAccount, isLoading: isAddingAccount } =
    MFMembers.useAddAccessReviewAccount();
  const { isOpen, onToggle } = useDisclosure();

  // New state for bulk actions
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [bulkRole, setBulkRole] = useState<Option | null>(null);
  const [bulkReviewType, setBulkReviewType] = useState<
    MFAccessReviewAccount["review"] | null
  >(null);
  const [outOfScopeReason, setOutOfScopeReason] = useState("");
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();

  const { mutate: reviewAccounts } = MFMembers.useReviewAccessReviewAccounts();

  const handleAddAccount = () => {
    if (!selectedMember || !selectedRole) {
      toast({
        title: "Please select both a member and a role",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    addAccount({
      accessReviewId: accessReviewId,
      appId: accessReviewWithTotal.review.app.id,
      memberId: selectedMember.value,
      role: [selectedRole.value],
    }).then(() => {
      toast({
        title: "Account added successfully",
        status: "success",
        variant: "subtle",
        duration: 3000,
        isClosable: true,
      });
      setSelectedMember(null);
      setSelectedRole(null);
    });
  };

  const handleBulkRoleChange = (newRole: Option | null) => {
    if (!newRole) return;

    reviewAccounts({
      accessAccountIds: selectedAccounts,
      accessReviewId,
      role: [newRole.value],
    })
      .then(() => {
        toast({
          title: "Roles updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setBulkRole(null);
        remove();
      })
      .catch((error) => {
        toast({
          title: "Failed to update roles",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });
  };

  const handleBulkReview = (review: MFAccessReviewAccount["review"]) => {
    setBulkReviewType(review);
    onConfirmOpen();
  };

  const submitBulkReview = () => {
    if (!bulkReviewType) return;

    reviewAccounts({
      accessAccountIds: selectedAccounts,
      accessReviewId,
      review: bulkReviewType,
      ...(bulkReviewType === "out_of_scope" ? { outOfScopeReason } : {}),
    })
      .then(() => {
        toast({
          title: "Accounts reviewed successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setSelectedAccounts([]);
        setBulkReviewType(null);
        setOutOfScopeReason("");
        onConfirmClose();
        remove();
      })
      .catch((error) => {
        toast({
          title: "Failed to review accounts",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });
  };

  return (
    <VStack w="full" align="flex-start" spacing={8}>
      <ReviewInfoSection
        accessReviewWithTotal={accessReviewWithTotal}
        accountIds={rows?.map((r) => r.id)}
      />

      <VStack w="full" spacing={4}>
        <HStack w="full" justify="space-between">
          <VStack spacing={0} align="flex-start">
            <Text fontSize="lg" color="gray.700" fontWeight="500">
              Accounts
            </Text>
            <Text fontSize="sm" color="gray.600">
              All the individual accounts a part of this review. Click on the
              column headings to filter.
            </Text>
          </VStack>
        </HStack>
        <HStack w="full" justify="space-between">
          <Wrap shouldWrapChildren>
            <Box minWidth="300px">
              <SearchBox
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
            <Box minW="175px">
              <FilterDropdown
                initialOptions={options?.departments || []}
                icon={MdLocalFireDepartment}
                placeholder="Department"
                value={
                  filters.department
                    ? filters.department.map((value) => ({
                        value: value,
                        label: value,
                      }))
                    : []
                }
                onChange={(values) => {
                  const selected = values.map(({ value }) => value);
                  setFilters((filters) => ({
                    ...filters,
                    department: selected,
                  }));
                }}
              />
            </Box>
            <FilterDropdown
              initialOptions={options?.job_titles || []}
              icon={FaIdBadge}
              placeholder="Job title"
              value={
                filters.job_title
                  ? filters.job_title.map((value) => ({
                      value: value,
                      label: value,
                    }))
                  : []
              }
              onChange={(values) => {
                const selected = values.map(({ value }) => value);
                setFilters((filters) => ({
                  ...filters,
                  job_title: selected,
                }));
              }}
            />
            <FilterDropdown
              initialOptions={roles || []}
              icon={FiUsers}
              placeholder="Role"
              value={
                filters.role
                  ? filters.role.map((value) => ({
                      value: value,
                      label: value,
                    }))
                  : []
              }
              onChange={(values) => {
                const selected = values.map(({ value }) => value);
                setFilters((filters) => ({
                  ...filters,
                  role: selected,
                }));
              }}
            />
            <FilterDropdown
              initialOptions={[
                { value: "required", label: "Required" },
                { value: "not_required", label: "Not required" },
                { value: "out_of_scope", label: "Out of scope" },
                { value: "removed", label: "Removed" },
              ]}
              icon={FiRewind}
              placeholder="Last review"
              value={
                filters.last_review
                  ? filters.last_review.map((value) => ({
                      value: value,
                      label: upperFirst(value.replaceAll("_", " ")) || "",
                    }))
                  : []
              }
              onChange={(values) => {
                const selected = values.map(({ value }) => value);
                setFilters((filters) => ({
                  ...filters,
                  last_review: selected,
                }));
              }}
            />
            <FilterDropdown
              initialOptions={[
                { value: "empty", label: "All" },
                { value: "true", label: "Reviewed" },
                { value: "false", label: "Unreviewed" },
              ]}
              icon={FiPlay}
              placeholder="Review status"
              value={
                filters.has_review === undefined
                  ? []
                  : Boolean(filters.has_review)
                    ? [{ value: "true", label: "Reviewed" }]
                    : [{ value: "false", label: "Unreviewed" }]
              }
              onChange={([{ value }]) => {
                switch (value) {
                  case "empty":
                    setFilters((f) => ({ ...f, has_review: undefined }));
                    break;
                  case "true":
                    setFilters((f) => ({ ...f, has_review: true }));
                    break;
                  case "false":
                    setFilters((f) => ({ ...f, has_review: false }));
                    break;
                }
              }}
              singleSelect
            />
          </Wrap>
          <HStack>
            {accessReviewWithTotal.missingRoles > 0 && (
              <Tag
                colorScheme={filters.missing_role ? "purple" : "gray"}
                _hover={{ transform: "scale(1.03)", cursor: "pointer" }}
                onClick={() =>
                  setFilters((f) => ({
                    ...f,
                    missing_role: !f.missing_role,
                  }))
                }
              >
                <TagLabel>
                  {accessReviewWithTotal.missingRoles} Missing Roles
                </TagLabel>
              </Tag>
            )}
            <Button
              style={{ marginLeft: "16px" }}
              leadingIcon={<Icon as={FiUserPlus} />}
              variant="solid"
              tone="primary"
              size="sm"
              onClick={onToggle}
            >
              Add user
            </Button>
          </HStack>
        </HStack>
        {isOpen && (
          <VStack w="full" align="flex-start" spacing={2}>
            <Text variant="label">Add User to Review</Text>
            <HStack w="full" spacing={4}>
              <Box flex={1}>
                <MultiSelect
                  options={options?.members || []}
                  value={selectedMember}
                  onChange={setSelectedMember}
                  placeholder="Select member"
                />
              </Box>
              <Box flex={1}>
                <MultiSelect
                  createable
                  options={roles}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  placeholder="Select role"
                />
              </Box>
              <Button
                onClick={handleAddAccount}
                isLoading={isAddingAccount}
                variant="solid"
                tone="primary"
                size="sm"
                leadingIcon={<Icon as={FiUserPlus} />}
                isDisabled={!selectedMember || !selectedRole}
              >
                Add
              </Button>
            </HStack>
          </VStack>
        )}

        {/* Bulk actions */}
        {selectedAccounts.length > 0 && (
          <VStack w="full" mb={6} spacing={4}>
            <Divider />
            <HStack w="full" justify="flex-end" spacing={4}>
              <Text variant="label">
                Bulk review ({selectedAccounts.length} selected out of{" "}
                {rows.length})
              </Text>
              <Box>
                <MultiSelect
                  options={roles}
                  value={bulkRole}
                  createable
                  onChange={(newRole) => {
                    setBulkRole(newRole);
                    handleBulkRoleChange(newRole);
                  }}
                  placeholder="Select role for bulk change"
                />
              </Box>
              <Button
                leadingIcon={<Icon as={FiCheckCircle} color="success.600" />}
                variant="outline"
                tone="neutral"
                size="sm"
                onClick={() => handleBulkReview("required")}
              >
                Mark as required
              </Button>
              <Tooltip label="Account removed. Select this when you have removed the account from the app/resource. If you haven't or are unable to remove the account yourself, but the member no longer needs then account, then select not required.">
                <Button
                  leadingIcon={<Icon as={FiTrash} color="error.600" />}
                  variant="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() => handleBulkReview("removed")}
                >
                  Mark as removed
                </Button>
              </Tooltip>
              <Tooltip label="Account not required. When the account is not required, but you can't / haven't removed the account.">
                <Button
                  leadingIcon={<Icon as={FiXCircle} color="error.600" />}
                  variant="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() => handleBulkReview("not_required")}
                >
                  Mark as not required
                </Button>
              </Tooltip>
              <Button
                leadingIcon={
                  <Icon as={HiOutlineMinusCircle} color="text.body.tertiary" />
                }
                variant="outline"
                tone="neutral"
                size="sm"
                onClick={() => handleBulkReview("out_of_scope")}
              >
                Mark as out of scope
              </Button>
            </HStack>
            <Divider />
          </VStack>
        )}

        <HStack w="full" spacing={4} align="baseline">
          <HStack w="5%" align="center">
            <Checkbox
              isChecked={selectedAccounts.length === rows.length}
              isIndeterminate={
                selectedAccounts.length > 0 &&
                selectedAccounts.length < rows.length
              }
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedAccounts(rows.map((r) => r.id));
                } else {
                  setSelectedAccounts([]);
                }
              }}
            />
          </HStack>
          <Box w="20%">
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Employee
            </Text>
          </Box>
          <VStack w="11.75%" align="baseline">
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Department
            </Text>
          </VStack>
          <VStack w="11.75%" align="baseline">
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Job title
            </Text>
          </VStack>
          <VStack w="25%" align="baseline">
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Role
            </Text>
          </VStack>
          <VStack w="11.75%" align="baseline">
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Last review
            </Text>
          </VStack>
          <Flex w="11.75%">
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Account Required
            </Text>
          </Flex>
        </HStack>
        <Divider />
        <VStack spacing={4} divider={<StackDivider />} w="full">
          {rows.length < 1 && filters.has_review === false && (
            <VStack w="full" h="full" pt={4} align="center" my={6}>
              <Flex
                borderRadius="100%"
                justify="center"
                align="center"
                w="32px"
                h="32px"
                bg="p.100"
                border="4px solid"
                borderColor="p.50"
              >
                <Icon as={FiUsers} color="p.700" />
              </Flex>
              <Text textAlign="center" variant="lg.sb">
                Well done!
              </Text>
              <Text textAlign="center" fontSize="sm" color="gray.600">
                You've got no accounts left to review!
              </Text>
            </VStack>
          )}
          {rows.map((r) => (
            <AccountRow
              key={r.id}
              account={r}
              canReview={true}
              roles={roles || []}
              isSelected={selectedAccounts.includes(r.id)}
              onSelect={(isSelected) => {
                if (isSelected) {
                  setSelectedAccounts([...selectedAccounts, r.id]);
                } else {
                  setSelectedAccounts(
                    selectedAccounts.filter((id) => id !== r.id),
                  );
                }
              }}
            />
          ))}
          {isLoading && <Spinner />}
          <div ref={loader} />
        </VStack>
      </VStack>
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose}>
        <ModalOverlay />
        <ModalContent borderRadius="12px">
          <ModalHeader>Confirm Action</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              You are about to update {selectedAccounts.length} account
              {selectedAccounts.length !== 1 ? "s" : ""}.
            </Text>
            {bulkReviewType === "required" && (
              <Text>
                Are you sure you want to mark these accounts as required?
              </Text>
            )}
            {bulkReviewType === "removed" && (
              <Text>
                Are you sure you want to mark these accounts as removed?
              </Text>
            )}
            {bulkReviewType === "out_of_scope" && (
              <VStack align="start" spacing={4}>
                <Text>
                  Please provide a reason for marking these accounts as out of
                  scope:
                </Text>
                <Input
                  value={outOfScopeReason}
                  onChange={(e) => setOutOfScopeReason(e.target.value)}
                  placeholder="Out of scope reason"
                />
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="subtle"
              tone="neutral"
              onClick={onConfirmClose}
              style={{ marginRight: "12px" }}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              tone={
                bulkReviewType === "required"
                  ? "success"
                  : bulkReviewType === "out_of_scope"
                    ? "secondary"
                    : "error"
              }
              onClick={submitBulkReview}
              isDisabled={
                bulkReviewType === "out_of_scope" && outOfScopeReason.length < 2
              }
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default AccountsList;
